<template>
  <div class="content">
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title">Permission System Test Page</h4>
              <p class="card-category">Test dynamic permission checking</p>
            </div>
            <div class="card-body">
              <!-- Permission Loading State -->
              <div v-if="permissionsLoading" class="alert alert-info">
                <i class="fas fa-spinner fa-spin"></i> Loading permissions...
              </div>

              <!-- User Info -->
              <div v-else class="mb-4">
                <h5>Your Current Permissions:</h5>
                <div class="permission-list">
                  <span v-for="perm in permissions" :key="perm" class="badge badge-info mr-2 mb-2">
                    {{ perm }}
                  </span>
                  <span v-if="permissions.length === 0" class="text-muted">No permissions loaded</span>
                </div>
                <p class="mt-3">
                  <strong>Admin Status:</strong> 
                  <span :class="userIsAdmin ? 'text-success' : 'text-muted'">
                    {{ userIsAdmin ? '✓ You are an admin' : '✗ You are not an admin' }}
                  </span>
                </p>
              </div>

              <hr>

              <!-- Test Permission Input -->
              <div class="test-section">
                <h5>Test Any Permission</h5>
                <p class="text-muted">Enter a permission key to check if you have it</p>
                
                <div class="form-group">
                  <label for="testPermission">Permission Key:</label>
                  <input
                    id="testPermission"
                    v-model="testPermission"
                    type="text"
                    class="form-control"
                    placeholder="e.g., test.view or retention.edit"
                    @keyup.enter="checkPermission"
                  />
                  <small class="form-text text-muted">
                    Format: resource.action (e.g., test.view, retention.edit, messages.send)
                  </small>
                </div>

                <button @click="checkPermission" class="btn btn-primary mb-3">
                  <i class="fas fa-check"></i> Check Permission
                </button>

                <!-- Result Display -->
                <div v-if="lastCheckedPermission" class="result-box">
                  <div v-if="hasTestPermission" class="alert alert-success">
                    <i class="fas fa-check-circle"></i>
                    <strong>✓ You HAVE the permission:</strong> 
                    <code>{{ lastCheckedPermission }}</code>
                  </div>
                  <div v-else class="alert alert-danger">
                    <i class="fas fa-times-circle"></i>
                    <strong>✗ You DO NOT have the permission:</strong> 
                    <code>{{ lastCheckedPermission }}</code>
                  </div>
                </div>
              </div>

              <hr>

              <!-- Quick Test Buttons -->
              <div class="test-section">
                <h5>Quick Permission Tests</h5>
                <p class="text-muted">Test common permissions with one click</p>
                
                <div class="permission-tests">
                  <div class="test-item">
                    <button @click="quickTest('test.view')" class="btn btn-sm btn-outline-primary">
                      Test: test.view
                    </button>
                    <span v-if="can('test.view')" class="result-badge badge-success">✓ Have</span>
                    <span v-else class="result-badge badge-danger">✗ Don't Have</span>
                  </div>

                  <div class="test-item">
                    <button @click="quickTest('test.create')" class="btn btn-sm btn-outline-primary">
                      Test: test.create
                    </button>
                    <span v-if="can('test.create')" class="result-badge badge-success">✓ Have</span>
                    <span v-else class="result-badge badge-danger">✗ Don't Have</span>
                  </div>

                  <div class="test-item">
                    <button @click="quickTest('retention.view')" class="btn btn-sm btn-outline-primary">
                      Test: retention.view
                    </button>
                    <span v-if="can('retention.view')" class="result-badge badge-success">✓ Have</span>
                    <span v-else class="result-badge badge-danger">✗ Don't Have</span>
                  </div>

                  <div class="test-item">
                    <button @click="quickTest('retention.edit')" class="btn btn-sm btn-outline-primary">
                      Test: retention.edit
                    </button>
                    <span v-if="can('retention.edit')" class="result-badge badge-success">✓ Have</span>
                    <span v-else class="result-badge badge-danger">✗ Don't Have</span>
                  </div>

                  <div class="test-item">
                    <button @click="quickTest('messages.send')" class="btn btn-sm btn-outline-primary">
                      Test: messages.send
                    </button>
                    <span v-if="can('messages.send')" class="result-badge badge-success">✓ Have</span>
                    <span v-else class="result-badge badge-danger">✗ Don't Have</span>
                  </div>

                  <div class="test-item">
                    <button @click="quickTest('admin.all')" class="btn btn-sm btn-outline-primary">
                      Test: admin.all
                    </button>
                    <span v-if="can('admin.all')" class="result-badge badge-success">✓ Have</span>
                    <span v-else class="result-badge badge-danger">✗ Don't Have</span>
                  </div>
                </div>
              </div>

              <hr>

              <!-- Advanced Tests -->
              <div class="test-section">
                <h5>Advanced Permission Tests</h5>
                
                <div class="advanced-test">
                  <h6>canAny() - Check if user has ANY of multiple permissions</h6>
                  <code>canAny(['test.view', 'test.create', 'test.edit'])</code>
                  <div class="mt-2">
                    <span v-if="canAny(['test.view', 'test.create', 'test.edit'])" class="badge badge-success">
                      ✓ You have at least one of these permissions
                    </span>
                    <span v-else class="badge badge-danger">
                      ✗ You don't have any of these permissions
                    </span>
                  </div>
                </div>

                <div class="advanced-test mt-3">
                  <h6>canAll() - Check if user has ALL permissions</h6>
                  <code>canAll(['retention.view', 'retention.edit'])</code>
                  <div class="mt-2">
                    <span v-if="canAll(['retention.view', 'retention.edit'])" class="badge badge-success">
                      ✓ You have all of these permissions
                    </span>
                    <span v-else class="badge badge-danger">
                      ✗ You don't have all of these permissions
                    </span>
                  </div>
                </div>

                <div class="advanced-test mt-3">
                  <h6>canAccess() - Resource-based permission check</h6>
                  <code>canAccess('retention', 'view')</code>
                  <div class="mt-2">
                    <span v-if="canAccess('retention', 'view')" class="badge badge-success">
                      ✓ You can access retention.view
                    </span>
                    <span v-else class="badge badge-danger">
                      ✗ You cannot access retention.view
                    </span>
                  </div>
                </div>
              </div>

              <hr>

              <!-- Instructions -->
              <div class="instructions">
                <h5>How to Add Test Permissions</h5>
                <ol>
                  <li>Go to the <strong>Admin Panel</strong> → <strong>Permission Types</strong> tab</li>
                  <li>Click <strong>"Add Permission Type"</strong></li>
                  <li>Fill in the form:
                    <ul>
                      <li><strong>Permission Key:</strong> test.view</li>
                      <li><strong>Category:</strong> Test</li>
                      <li><strong>Display Name:</strong> View Test</li>
                      <li><strong>Description:</strong> Can view test page</li>
                    </ul>
                  </li>
                  <li>Go to <strong>Roles & Permissions</strong> tab</li>
                  <li>Edit your role and add the <code>test.view</code> permission</li>
                  <li>Refresh this page to see the changes!</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PermissionTest',
  data() {
    return {
      testPermission: '',
      lastCheckedPermission: '',
      hasTestPermission: false
    };
  },
  methods: {
    checkPermission() {
      if (!this.testPermission) {
        alert('Please enter a permission to check');
        return;
      }
      
      this.lastCheckedPermission = this.testPermission;
      this.hasTestPermission = this.can(this.testPermission);
    },
    quickTest(permission) {
      this.testPermission = permission;
      this.checkPermission();
    }
  }
};
</script>

<style scoped>
.card {
  background-color: #27293d;
  border: 1px solid #2b3553;
}

.card-header {
  border-bottom: 1px solid #2b3553;
}

.card-title {
  color: #ffffff;
  font-weight: 600;
}

.card-category {
  color: #9A9A9A;
  margin-bottom: 0;
}

.permission-list {
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  min-height: 60px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.badge {
  font-size: 0.85em;
  padding: 6px 12px;
  font-weight: 500;
}

.badge-info {
  background-color: #1d8cf8;
  color: #ffffff;
}

.badge-success {
  background-color: #00bf9a;
  color: #ffffff;
}

.badge-danger {
  background-color: #fd5d93;
  color: #ffffff;
}

.test-section {
  margin: 30px 0;
}

.test-section h5 {
  color: #ffffff;
  margin-bottom: 15px;
  font-weight: 600;
}

.form-control {
  background-color: #2b3553;
  border: 1px solid #2b3553;
  color: #ffffff;
  padding: 12px;
  font-size: 14px;
}

.form-control:focus {
  background-color: #2b3553;
  border-color: #1d8cf8;
  color: #ffffff;
  box-shadow: 0 0 0 0.2rem rgba(29, 140, 248, 0.25);
}

.form-control::placeholder {
  color: #6c757d;
}

.form-text {
  color: #9A9A9A;
}

.result-box {
  margin-top: 20px;
}

.alert {
  padding: 15px 20px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.alert-success {
  background-color: rgba(0, 191, 154, 0.15);
  border: 1px solid rgba(0, 191, 154, 0.3);
  color: #00bf9a;
}

.alert-danger {
  background-color: rgba(253, 93, 147, 0.15);
  border: 1px solid rgba(253, 93, 147, 0.3);
  color: #fd5d93;
}

.alert-info {
  background-color: rgba(29, 140, 248, 0.15);
  border: 1px solid rgba(29, 140, 248, 0.3);
  color: #1d8cf8;
}

.alert code {
  background: rgba(255, 255, 255, 0.1);
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.permission-tests {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
}

.test-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid #2b3553;
}

.result-badge {
  margin-left: auto;
  padding: 5px 12px;
  font-size: 0.8em;
  border-radius: 4px;
  font-weight: 600;
}

.btn-outline-primary {
  color: #1d8cf8;
  border-color: #1d8cf8;
  background-color: transparent;
}

.btn-outline-primary:hover {
  color: #ffffff;
  background-color: #1d8cf8;
  border-color: #1d8cf8;
}

.advanced-test {
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border-left: 3px solid #1d8cf8;
}

.advanced-test h6 {
  color: #ffffff;
  margin-bottom: 10px;
}

.advanced-test code {
  background: rgba(255, 255, 255, 0.1);
  padding: 5px 10px;
  border-radius: 4px;
  color: #1d8cf8;
  display: inline-block;
}

.instructions {
  padding: 20px;
  background: rgba(29, 140, 248, 0.1);
  border-radius: 6px;
  border: 1px solid rgba(29, 140, 248, 0.3);
}

.instructions h5 {
  color: #1d8cf8;
  margin-bottom: 15px;
}

.instructions ol {
  color: #ffffff;
  padding-left: 20px;
}

.instructions li {
  margin-bottom: 10px;
  line-height: 1.6;
}

.instructions ul {
  margin-top: 8px;
  color: #e3e3e3;
}

.instructions code {
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: 3px;
  color: #1d8cf8;
  font-weight: 600;
}

.text-success {
  color: #00bf9a !important;
}

.text-muted {
  color: #9A9A9A !important;
}

hr {
  border-top: 1px solid #2b3553;
}
</style>

